import { api } from "@/lib/api";
import { toast } from "sonner";


/** Convert base64url string → Uint8Array (for VAPID applicationServerKey). */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}


/** Register the push service worker (idempotent). */
export async function ensurePushSw() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  try {
    let reg = await navigator.serviceWorker.getRegistration("/sw-notifications.js");
    if (!reg) reg = await navigator.serviceWorker.register("/sw-notifications.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (err) { console.debug("[push] sw register failed", err); return null; }
}


/** Ask for notification permission + subscribe to Web Push. Returns bool success. */
export async function subscribeToPush(vapidPublicKey) {
  if (!vapidPublicKey) { toast.error("Push not configured on the server"); return false; }
  if (!("Notification" in window)) { toast.error("Browser doesn't support notifications"); return false; }
  if (Notification.permission === "denied") {
    toast.error("Notifications are blocked in browser settings");
    return false;
  }
  const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (perm !== "granted") { toast.info("Notification permission not granted"); return false; }
  const reg = await ensurePushSw();
  if (!reg) { toast.error("Service worker unavailable"); return false; }
  try {
    const existing = await reg.pushManager.getSubscription();
    let sub = existing;
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }
    const json = sub.toJSON();
    await api.post("/notifications/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys || {},
    });
    return true;
  } catch (e) {
    toast.error("Push subscription failed: " + (e.message || "unknown"));
    return false;
  }
}


export async function unsubscribeFromPush() {
  const reg = await ensurePushSw();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const json = sub.toJSON();
  try {
    await api.post("/notifications/push/unsubscribe", {
      endpoint: json.endpoint,
      keys: json.keys || {},
    });
  } catch (err) {
    // Server may not have this subscription any more — safe to ignore, but log for debugging.
    console.debug("[push] unsubscribe backend call failed", err?.response?.status || err);
  }
  await sub.unsubscribe();
}
